import { AxiosResponse } from "axios";
import { getBase64 } from "./ImportBusService";
import * as ApiUrls from "./ApiUrl.Import";
import { axiosCoreInstance } from "../AxiosCoreInterceptor";
import { DataImportType, ImportActions } from "../../Model/Enum/BEE123Enums";
import { Import } from "../../Model/Dto/Import";
import { defaultObject } from "../../Model/helpers";
import { ExportKeyValue } from "../../Model/Dto/Upload";
import { ImportResult } from "../../../Modules/VerificationAgency/VerificationProcess/VAClientImportManagement/MultiPageImport";
import { DynamicCsvRequest } from "../../Model/Dto/DynamicCsv";
import { toast } from "react-toastify";
import {ImportSource} from "../../Model/Enum/ImportEnum";

interface ImportData {
    importType: DataImportType;
    scorecardId?: string;
    importAction: number;
    importSource: ImportSource;
    file: any;
    parameters : ExportKeyValue[];
    options?: any;
}

export interface CentralImportData {
    scorecardIds: string[];
    importAction: number;
    importSource: ImportSource;
    importType: DataImportType;
    file: any;
    parameters: ExportKeyValue[];
    setPercentageUpload: any;
}

export interface MultiPageImportData {
    dataImportTypes: DataImportType[];
    scorecardId: string|null;
    importAction: ImportActions;
    organisationId: number|null;
    file: any;
    parameters : ExportKeyValue[];
    options?: any;
    scorecardType?: number;
}

export interface EmploymentEquityGroupImportData {
    scorecardIds: string[];
    importAction: ImportActions;
    importSource: ImportSource;
    importType: DataImportType;
    file: any;
    parameters: ExportKeyValue[];
}

export interface PayrollImportData {
    beeScorecardIds: string[];
    eeScorecardIds: string[];
    parameters: ExportKeyValue[];
    organisationId: number;
    requestId: number;
}

export const CoreImportService = {
    ImportFile: async (data: ImportData): Promise<AxiosResponse<any>> => {
        let fileModel : Import.FileImportModel = defaultObject;
        await getBase64(data.file).then((result : any) => {
            fileModel = {
                scorecardId: data.scorecardId,
                importAction: data.importAction,
                importSource: data.importSource,
                importType: data.importType,
                fileData: {
                    name: data.file.name,
                    size: data.file.size,
                    type: data.file.type,
                    content: result
                },
                parameters: data.parameters
            }
        });
        return axiosCoreInstance.post(ApiUrls.Import, fileModel);
    },

    ImportCentralFile: async (data: CentralImportData): Promise<AxiosResponse<any>> => {
        const options = {
            onUploadProgress: (progressEvent: any) => {
                const { loaded, total } = progressEvent;
                let percent = Math.floor((loaded * 100) / total)
                data.setPercentageUpload(percent);
            },
        }
        let fileModel : Import.CentralFileImportModel = defaultObject;
        await getBase64(data.file)
        .then((result: any) => {
            fileModel = {
            scorecardIds: data.scorecardIds,
            importAction: data.importAction,
            importSource: data.importSource,
            importType: data.importType,
            fileData: {
                name: data.file.name,
                size: data.file.size,
                type: data.file.type,
                content: result
            },
            parameters: data.parameters
            };
        })
        .catch((error) => {
            console.error("Error during base64 conversion:", error);
            toast.error("Please reupload the updated file before importing again.");
            throw error; 
        });
        return axiosCoreInstance.post(ApiUrls.CentralImport, fileModel, options);
    },

    ImportMultiPageExcelFile: async (data: MultiPageImportData): Promise<AxiosResponse<ImportResult>> => {
        let fileModel : Import.MultiPageFileImportModel = defaultObject;
        await getBase64(data.file)
        .then((result : any) => {
            fileModel = {
                scorecardId: data.scorecardId,
                importAction: data.importAction,
                dataImportTypes: data.dataImportTypes,
                organisationId: data.organisationId,
                fileData: {
                    name: data.file.name,
                    size: data.file.size,
                    type: data.file.type,
                    content: result 
                },
                parameters: data.parameters,
                scorecardType: data.scorecardType
            };
        })
        .catch((error) => {
            console.error("Error during base64 conversion:", error);
            toast.error("Please reupload the updated file before importing again.");
            throw error; 
        });
        return axiosCoreInstance.post(ApiUrls.MultiDataPageImport, fileModel);
    },

    ImportEmploymentEquityGroupFile: async (data: EmploymentEquityGroupImportData): Promise<AxiosResponse<ImportResult>> => {
        let fileModel : Import.EmploymentEquityGroupImportModel = defaultObject;
        await getBase64(data.file)
        .then((result : any) => {
            fileModel = {
                scorecardIds: data.scorecardIds,
                importAction: data.importAction,
                importSource: data.importSource,
                importType: data.importType,
                fileData: {
                    name: data.file.name,
                    size: data.file.size,
                    type: data.file.type,
                    content: result
                },
                parameters: data.parameters
            };
        }).catch((error) => {
            console.error("Error during base64 conversion:", error);
            toast.error("Please reupload the updated file before importing again.");
            throw error; 
        });
        return axiosCoreInstance.post(ApiUrls.EmploymentEquityGroupImport, fileModel);
    },

    GenerateSalaryExcelImportTemplate: (request: DynamicCsvRequest): Promise<AxiosResponse<any>> => axiosCoreInstance.post(ApiUrls.GenerateSalaryExcelImportTemplate, request, {method: 'GET', responseType: 'blob'}),
    ImportPayrollData: async (data: PayrollImportData): Promise<AxiosResponse<any>> => axiosCoreInstance.post(ApiUrls.PayrollImport, data),
}